import { ActionFunction, replace } from 'react-router-dom';
import { client } from '~/api/client';
import { parseVisibilityFormField } from '~/api/data/form-data';
import { mutateData } from '~/api/data/request';
import { submitMutation } from '~/api/data/responses';
import {
  CaraWorkspaceEffortLevel,
  CaraWorkspacePreset,
  CreateCustomPresetDocument,
  CreateCustomPresetMutation,
  CreateCustomPresetMutationVariables,
  UpdateCustomPresetDocument,
  UpdateCustomPresetMutation,
  UpdateCustomPresetMutationVariables,
  UpdateCustomPresetsDocument,
  UpdateCustomPresetsMutation,
  UpdateCustomPresetsMutationVariables
} from '~/api/graphql';

export const createCustomPreset = (variables: CreateCustomPresetMutationVariables) =>
  mutateData<CreateCustomPresetMutation, CreateCustomPresetMutationVariables>(client, {
    mutation: CreateCustomPresetDocument,
    variables
  });

export const updateCustomPreset = (variables: UpdateCustomPresetMutationVariables) =>
  mutateData<UpdateCustomPresetMutation, UpdateCustomPresetMutationVariables>(client, {
    mutation: UpdateCustomPresetDocument,
    variables
  });

export const archiveCustomPresets = (variables: UpdateCustomPresetsMutationVariables) =>
  mutateData<UpdateCustomPresetsMutation, UpdateCustomPresetsMutationVariables>(client, {
    mutation: UpdateCustomPresetsDocument,
    variables
  });

export const submitArchiveCustomPresets: ActionFunction = async ({ request }) =>
  submitMutation(
    archiveCustomPresets({
      presetIds: (await request.json()).presetIds,
      input: { archive: true }
    })
  );

export const submitCreateCustomPreset: ActionFunction = async ({ request }) => {
  const formData = await request.formData();
  return submitMutation(
    createCustomPreset({
      input: {
        allowFileUpload: formData.get('allowFileUpload') === 'true',
        basePrompt: formData.get('basePrompt') as string,
        customFormIds: formData.getAll('customFormIds') as string[] | null,
        defaultEffortLevel: formData.get('defaultEffortLevel') as CaraWorkspaceEffortLevel | null,
        description: formData.get('description') as string,
        excelSpreadsheetTemplateIds: formData.getAll('excelSpreadsheetTemplateIds') as
          | string[]
          | null,
        fileIds: formData.getAll('fileIds') as string[] | null,
        htmlDocumentTemplateIds: formData.getAll('htmlDocumentTemplateIds') as string[] | null,
        inheritFromPreset: formData.get('inheritFromPreset') as CaraWorkspacePreset | null,
        title: formData.get('title') as string,
        uploadFiles: formData.getAll('uploadFiles') as File[],
        visibility: parseVisibilityFormField(formData.get('visibility')),
        wordDocumentTemplateIds: formData.getAll('wordDocumentTemplateIds') as string[] | null
      }
    }),
    async () => replace(`/app/presets`)
  );
};

export const submitUpdateCustomPreset: ActionFunction = async ({ params, request }) => {
  const formData = await request.formData();
  return submitMutation(
    updateCustomPreset({
      id: params.id || '',
      input: {
        allowFileUpload: formData.get('allowFileUpload') === 'true',
        basePrompt: formData.get('basePrompt') as string,
        customFormIds: formData.getAll('customFormIds') as string[] | null,
        defaultEffortLevel: formData.get('defaultEffortLevel') as CaraWorkspaceEffortLevel | null,
        description: formData.get('description') as string,
        excelSpreadsheetTemplateIds: formData.getAll('excelSpreadsheetTemplateIds') as
          | string[]
          | null,
        fileIds: formData.getAll('fileIds') as string[] | null,
        htmlDocumentTemplateIds: formData.getAll('htmlDocumentTemplateIds') as string[] | null,
        inheritFromPreset: formData.get('inheritFromPreset') as CaraWorkspacePreset | null,
        title: formData.get('title') as string,
        uploadFiles: formData.getAll('uploadFiles') as File[],
        visibility: parseVisibilityFormField(formData.get('visibility')),
        wordDocumentTemplateIds: formData.getAll('wordDocumentTemplateIds') as string[] | null
      }
    }),
    async () => replace(`/app/presets`)
  );
};
